import { useState } from 'react';
import ErrorBadRequest from '@/interfaces/errors/badRequest';
import { auth } from 'config/firebase';
import usersApi from '@/services/api/users';
import newsletter from '@/services/api/newsletter';
import metrics from '@/services/metrics';
import * as googleAuth from '@/services/auth/google';
import * as errorsUtils from './errors';
import { trackNewUserSignup } from './utils';
import { measureDiffSecs } from '@/common/utils/perf';
const PREFIX = 'features: Auth: hooks: useOnSubmit:';
const USER_ERROR_MESSAGES = {
  name: 'Invalid name provided',
  email: 'Invalid email provided',
  password: 'Invalid password provided',
  team: 'Team not accessable to join',
  unknown: 'Unknown issue occured during signup please try again'
};
export default function useOnSubmit() {
  const {
    0: isGoogleSignupLoading,
    1: setGoogleSignupLoading
  } = useState(false);
  const {
    0: isLoading,
    1: setIsLoading
  } = useState(false);
  const {
    0: errors,
    1: setErrors
  } = useState({});

  const onSubmit = async (reCaptchaToken, data) => {
    setIsLoading(true);
    setErrors({}); // Reset errors
    // Create auth & database user

    let user;

    try {
      user = await usersApi.createRecord(reCaptchaToken, data);
    } catch (err) {
      setIsLoading(false);

      if (errorsUtils.hasExistingAccountError(err)) {
        throw errorsUtils.createAccountExistsError(`${PREFIX} useOnSubmit:`, err);
      } else {
        setUserErrorMessages(err);
        throw errorsUtils.createUserRecordError(`${PREFIX} onSubmit:`, err);
      }
    } // Track new user sign up


    trackNewUserSignup(); // Auto sign in user

    const password = data.password || '';

    try {
      await auth.signInWithEmailAndPassword(data.email, password);
    } catch (err) {
      setIsLoading(false);
      throw errorsUtils.createSignInError(`${PREFIX} onSubmit:`, err);
    } // Send user verification email


    try {
      if (!auth.currentUser) throw Error('auth.currentUser not set');
      await auth.currentUser.sendEmailVerification();
    } catch (err) {
      setIsLoading(false);
      throw errorsUtils.createEmailVerificationError(`${PREFIX} onSubmit:`, err);
    } // Subscribe user to newsletter


    if (data.newsletter) {
      subscribeToNewsletter(user);
    }

    setIsLoading(false);
    return user;
  };

  const onSubmitWithGoogle = async (reCaptchaToken, credentialToken, newsletter) => {
    setGoogleSignupLoading(true);
    setErrors({}); // Reset errors

    const userData = {
      name: '',
      email: '',
      team: '',
      fromGoogle: true
    }; // Start the timer to measure the time it takes to sign up

    const getSignupTime = measureDiffSecs();

    try {
      const {
        user
      } = credentialToken ? await googleAuth.signInWithCredential(credentialToken) : await googleAuth.signInWithPopup();
      userData.name = (user === null || user === void 0 ? void 0 : user.displayName) || '';
      userData.email = (user === null || user === void 0 ? void 0 : user.email) || '';
    } catch (err) {
      setErrors({
        'Unknown error': USER_ERROR_MESSAGES.unknown
      });
      setGoogleSignupLoading(false);
      throw errorsUtils.createSignUpWithGoogleError(`${PREFIX} onSubmitWithGoogle:`, err);
    } // Track google sign up time


    metrics.sendEvent('google_sign_up_seconds', {
      google_sign_up_seconds: getSignupTime()
    }); // Create auth & database user

    let user;

    try {
      user = await usersApi.createRecord(reCaptchaToken, userData);
    } catch (err) {
      if (errorsUtils.hasExistingAccountError(err)) {
        throw errorsUtils.createAccountExistsError(`${PREFIX} onSubmitWithGoogle:`, err);
      } else {
        setUserErrorMessages(err);
        throw errorsUtils.createUserRecordError(`${PREFIX} onSubmitWithGoogle:`, err);
      }
    } finally {
      setGoogleSignupLoading(false);
    } // Track new user sign up


    trackNewUserSignup(); // Subscribe user to newsletter

    if (newsletter) {
      subscribeToNewsletter(user);
    }

    return userData;
  };

  const subscribeToNewsletter = async data => {
    try {
      await newsletter.subscribe({
        email: data.email,
        firstName: data.name,
        team: data.teamId,
        user: data.uid,
        tags: ['User']
      });
    } catch (err) {
      throw errorsUtils.createNewsletterSubscribeError(`${PREFIX} subscribeToNewsletter:`, err);
    }
  };

  const setUserErrorMessages = err => {
    if (err instanceof ErrorBadRequest === false) return;
    const errors = (err.errors || []).reduce((acc, _ref) => {
      let {
        name,
        detail
      } = _ref;
      acc[name] = USER_ERROR_MESSAGES[name] || detail || 'Unknown issue';
      return acc;
    }, {});
    setErrors(errors);
  };

  return {
    errors,
    isLoading,
    isGoogleSignupLoading,
    onSubmit,
    onSubmitWithGoogle
  };
}